// ─── E.164 Phone Number Utilities (US-only) ─────────────
//
// Strict policy: input must normalize to exactly 10 NANP digits. Accepting "10
// digits, prepend +1" without checking the first digit caused a double-prefix
// bug — e.g. user editing "+19293454041" (already malformed: 11 digits) saw
// "(192) 934-5404" in the form, the form sent "1929345404", and toE164 produced
// "+11929345404". NANP area codes start 2–9, so guarding against a leading "1"
// makes that path fail loudly instead of silently corrupting the stored number.
const NANP_NATIONAL_RE = /^[2-9]\d{2}[2-9]\d{6}$/;
/**
 * Strip to digits, accept exactly 10 NANP-valid digits OR 11 starting with "1"
 * (country code) wrapping a NANP-valid 10. Throws if the result isn't a valid
 * US/NANP number.
 */
export function toE164(raw) {
    const national = extractNationalDigits(raw);
    if (national === null) {
        throw new Error(`Invalid US phone number: ${raw}`);
    }
    return `+1${national}`;
}
/** Returns the 10-digit NANP national number, or null if input isn't valid. */
function extractNationalDigits(raw) {
    if (typeof raw !== "string") {
        return null;
    }
    const digits = raw.replace(/\D/g, "");
    let national;
    if (digits.length === 10) {
        national = digits;
    }
    else if (digits.length === 11 && digits.startsWith("1")) {
        national = digits.slice(1);
    }
    else {
        return null;
    }
    return NANP_NATIONAL_RE.test(national) ? national : null;
}
/** True iff `raw` normalizes to a valid +1<NANP> E.164 number. */
export function isValidUsPhone(raw) {
    return extractNationalDigits(raw) !== null;
}
/** Like toE164, but returns null on invalid input instead of throwing. */
export function tryToE164(raw) {
    const national = extractNationalDigits(raw);
    return national === null ? null : `+1${national}`;
}
/** +12125551234 → +1 (212) 555-1234 — for web/mobile UIs where the "+1"
 *  prefix matches the input chip pattern (agency portal, admin, mobile). */
export function formatPhoneDisplay(e164) {
    const digits = e164.replace(/\D/g, "").slice(-10);
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
/** +12125551234 → (212) 555-1234 — for printed PDFs where the "+1" prefix
 *  is omitted per print/regulatory convention (CMS-485, POC, Welcome, etc.). */
export function formatPhoneForPdf(e164) {
    const digits = e164.replace(/\D/g, "").slice(-10);
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
/** Check if a string looks like a phone number (10+ digits when non-digits stripped) */
export function isPhone(value) {
    const digits = value.replace(/\D/g, "");
    const last10 = digits.length > 10 ? digits.slice(-10) : digits;
    return last10.length === 10;
}
//# sourceMappingURL=phone.js.map